import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { ScrollArea } from "@arcadeai/design-system/components/ui/atoms/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@arcadeai/design-system/components/ui/atoms/sheet";
import { Skeleton } from "@arcadeai/design-system/components/ui/atoms/skeleton";
import { Form } from "@arcadeai/design-system/rhf";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { Suspense, useCallback, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useCreateCatalogGrant } from "../api/hooks";
import { CATALOG_ROUTE_ID, catalogSearchDefaults } from "../search-params";
import {
  type CatalogGrantCreateInput,
  catalogGrantCreateSchema,
} from "../types";
import { SubjectField } from "./form/subject-field";
import { ToolRefsField } from "./form/tool-refs-field";

const DEFAULT_VALUES: CatalogGrantCreateInput = {
  subject: "",
  tool_refs: [],
};

type CatalogGrantSheetProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export const CatalogGrantSheet = ({
  open,
  onOpenChange,
}: CatalogGrantSheetProps) => {
  const { create } = useSearch({ from: CATALOG_ROUTE_ID });
  const navigate = useNavigate();

  const form = useForm<CatalogGrantCreateInput>({
    resolver: zodResolver(catalogGrantCreateSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const { mutate: createGrant, isPending } = useCreateCatalogGrant();

  const cleanUrl = useCallback(() => {
    if (create) {
      navigate({ to: ".", search: catalogSearchDefaults, replace: true });
    }
  }, [create, navigate]);

  // Reset the form whenever the sheet transitions from closed to open, so a
  // reopened sheet never shows stale values from a previous grant.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      form.reset(DEFAULT_VALUES);
    }
    wasOpenRef.current = open ?? false;
  }, [open, form]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      onOpenChange?.(next);
      if (!next) {
        cleanUrl();
      }
    },
    [onOpenChange, cleanUrl]
  );

  const handleSubmit = useCallback(
    (values: CatalogGrantCreateInput) => {
      createGrant(values, {
        onSuccess: () => handleOpenChange(false),
      });
    },
    [createGrant, handleOpenChange]
  );

  const handleError = useCallback(() => {
    toast.error("Some required information is missing or incorrect");
  }, []);

  return (
    <Sheet onOpenChange={handleOpenChange} open={open}>
      <SheetContent className="w-full px-2">
        <ScrollArea className="h-full w-full">
          <SheetHeader>
            <SheetTitle className="mt-2">Add end-user tool grant</SheetTitle>
            <SheetDescription>
              Publish tools for a specific IdP group — or for everyone — to
              self-serve.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 px-4">
            <Form {...form}>
              <form
                className="space-y-6"
                onSubmit={form.handleSubmit(handleSubmit, handleError)}
              >
                <SubjectField control={form.control} />
                <Suspense fallback={<ToolRefsFieldSkeleton />}>
                  <ToolRefsField control={form.control} />
                </Suspense>
                <Button
                  className="mt-3 w-full"
                  disabled={isPending}
                  type="submit"
                >
                  <span className="flex items-center justify-center gap-3">
                    {!!isPending && <Loader2 className="size-4 animate-spin" />}
                    <span>Add grant</span>
                  </span>
                </Button>
              </form>
            </Form>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

function ToolRefsFieldSkeleton() {
  return (
    <div className="space-y-2">
      <Skeleton className="h-3.5 w-12" />
      <Skeleton className="h-9 w-full rounded-md" />
      <Skeleton className="h-3 w-56" />
    </div>
  );
}
